import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { getDealStageTone, getEnumLabel } from '../../../shared/format/enum-labels.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import { type ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { TagChip } from '../../../components/entity-detail/TagChip.js';

import { formatMoney, formatPersonName } from '../../../shared/format/index.js';
import type { InheritedLink } from '../../../shared/ui/LinkCell.js';
import type { DealCompanyRef, DealListItem } from '../api/deals.client.js';

/** Подпись компании: собственная у сделки или унаследованная от контакта. */
function companyLabel(company: DealCompanyRef | InheritedLink): string {
  return 'name' in company ? company.name : company.label;
}

export interface DealCardsGridProps {
  deals: DealListItem[];
}

/**
 * Карточный (grid) режим списка сделок. Те же серверные фильтры и выборка, что у
 * канбана и списка, — компонент только раскладывает полученные сделки по карточкам.
 * Каждая карточка — ссылка на карточку сделки.
 */
export function DealCardsGrid({ deals }: DealCardsGridProps): ReactElement {
  useI18nSubscription();
  const location = useLocation();

  return (
    <div
      data-testid="deals-cards-grid"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
    >
      {deals.map((deal) => {
        const company = deal.company ?? deal.inheritedLinks?.company;
        return (
          <Link
            key={deal.id}
            to={`/deals/${deal.id}`}
            state={{ dealListUrl: location.pathname + location.search }}
            data-testid={`deal-card-${deal.id}`}
            className="block rounded-lg border p-3 text-sm transition-colors hover:border-[var(--ant-color-border-hover)] hover:bg-[var(--ant-color-bg-elevated)]"
            style={{
              borderColor: 'var(--ant-color-border)',
              backgroundColor: 'var(--ant-color-bg-elevated)',
            }}
          >
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <span
                className="truncate rounded font-medium"
                style={{
                  padding: '2px 6px',
                  fontSize: 10,
                  backgroundColor: 'var(--g-fill)',
                  color: 'var(--ant-color-text-secondary)',
                }}
              >
                {formatPersonName(
                  deal.contact?.firstName,
                  deal.contact?.lastName,
                  i18n.t('crm.features.features.deals.components.deallistview.контакт_не_указан'),
                )}
              </span>
              <EntityStatusChip tone={getDealStageTone(deal.stage)} style={{ margin: 0 }}>
                {getEnumLabel('dealStage', deal.stage, i18n.t)}
              </EntityStatusChip>
            </div>

            <h4
              className="mb-1.5 font-semibold text-[var(--ant-color-text)]"
              style={{ fontSize: 13, lineHeight: 1.3, wordBreak: 'break-word' }}
            >
              {deal.title}
            </h4>

            <div className="flex items-center justify-between gap-2">
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--ant-color-text-tertiary)',
                  fontFamily: 'monospace',
                }}
              >
                {formatMoney(deal.amount, deal.currency)}
              </span>
              {deal.tags.length > 0 ? (
                <span className="flex flex-wrap gap-1">
                  {deal.tags.map((tag) => (
                    <TagChip key={tag} value={tag} />
                  ))}
                </span>
              ) : null}
            </div>

            {company !== null && company !== undefined ? (
              <div
                className="mt-2 truncate text-xs text-[var(--ant-color-text-secondary)]"
                title={companyLabel(company)}
              >
                {companyLabel(company)}
              </div>
            ) : null}
          </Link>
        );
      })}
    </div>
  );
}
